import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
    selectCurrentDrawerId,
    selectCurrentEntity,
    selectCurrentTenantId,
    selectNameInConflict,
    selectOpen,
    setNameInConflict,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { MembershipLinkingDrawerContent } from 'common/components/sharedCustomComponents/MembershipLinkingDrawer/MembershipLinkingDrawerContent';
import { useDrawerSubmit } from 'common/hooks/useDrawerSubmit';
import { fetchRoles } from 'common/services/api/Roles/Roles.thunk';
import { postUserProfileRoleMembership } from 'common/services/api/UserProfileRoles/UserProfileRoles.thunk';
import { postUserRoleMembership } from 'common/services/api/UserRoleMemberships/UserRoleMemberships.thunk';
import { Role } from 'common/types/Entities';
import { DrawerEntitiesNames, EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { displayRoleOptionMenuItemContent } from 'common/utils/RoleEntitiesDropDownHelpers';
import { updateUserProfileTenant } from 'features/userProfileApps/helpers';
import { selectUserDetails } from 'features/users/usersSlice';

const USER_ROLE_MEMBERSHIP_POST_SUCCESS_MESSAGE = 'The Role was successfully linked.';
const EXISTING_ROLE_LINKING_ERROR_MESSAGE = 'This Role is already linked to this User.';
const INITIAL_ROLE_OPTION: Role = { id: '', name: '', tenantId: '' };

const getUserFullName = (firstName = 'Unknown', lastName = 'User'): string => {
    return `${firstName} ${lastName}`;
};

export function UserRoleMembershipCreateContent({
    tenant,
    title,
}: Readonly<EditDrawerContentProps>) {
    const [role, setRole] = useState<Role | null>(null);

    const nameInConflict = useAppSelector(selectNameInConflict);
    const pageInfo = useAppSelector(selectPageInfo);
    const drawerOpen = useAppSelector(selectOpen);
    const currentId = useAppSelector(selectCurrentDrawerId);
    const dispatch = useAppDispatch();
    const currentEntity = useAppSelector(selectCurrentEntity);
    const currentTenantId = useAppSelector(selectCurrentTenantId);
    const userDetails = useAppSelector(selectUserDetails);

    const { processing, handleSubmit: handleUserRoleMembershipSubmit } = useDrawerSubmit({
        submitThunk: postUserRoleMembership,
        entityName: 'Role',
        successMessage: USER_ROLE_MEMBERSHIP_POST_SUCCESS_MESSAGE,
        operation: Operations.Link,
        onSuccess: (result) => {
            dispatch(setRows(result.rows));
        },
    });

    const { processing: profileProcessing, handleSubmit: handleProfileRoleMembershipSubmit } = useDrawerSubmit({
        submitThunk: postUserProfileRoleMembership,
        entityName: 'Role',
        successMessage: USER_ROLE_MEMBERSHIP_POST_SUCCESS_MESSAGE,
        operation: Operations.Link,
        onSuccess: () => {
            updateUserProfileTenant({
                tenantId: tenant?.id ?? '',
                userId: userDetails?.userId ?? '',
            });
        },
    });

    useEffect(() => {
        if(currentId === DrawerIDs.UserRoleMembership){
            setRole(null);
            dispatch(setSaveButtonEnabled(false));
        }
    }, [drawerOpen]);

    const handleRoleChange = (_e: unknown, newRole: Role | null) => {
        setRole(newRole);
        dispatch(setSaveButtonEnabled(!!newRole?.id));
        dispatch(setNameInConflict(false));
    };

    const userRoleMembershipPostCallback = () => {
        handleUserRoleMembershipSubmit({
            tenantId: pageInfo?.id ?? '',
            userId: pageInfo?.userId ?? '',
            roleId: role?.id ?? ''
        });
    };

    const userProfileUserRoleMembershipPostCallback = () => {
        handleProfileRoleMembershipSubmit({
            tenantId: tenant?.id ?? '',
            userId: userDetails?.userId ?? '',
            roleId: role?.id ?? ''
        });
    };

    const isProcessing = processing || profileProcessing;
    const hasConflict = nameInConflict ?? false;

    const renderDrawerContent = (
        tenantId: string,
        tenantName: string,
        userFullName: string,
        saveCallback: () => void
    ) => (
        <MembershipLinkingDrawerContent<Role>
            title={title}
            processing={isProcessing}
            saveButtonFunction={saveCallback}
            entity={role}
            onEntityChange={handleRoleChange}
            tenantId={tenantId}
            tenantName={tenantName}
            userFullName={userFullName}
            nameInConflict={hasConflict}
            conflictErrorMessage={EXISTING_ROLE_LINKING_ERROR_MESSAGE}
            dropdownLabel="Tenant Role Search"
            initialEntityOption={INITIAL_ROLE_OPTION}
            asyncThunkAction={fetchRoles}
            displayMenuItemContent={displayRoleOptionMenuItemContent}
        />
    );

    switch(currentEntity) {
        case DrawerEntitiesNames.AppSuiteUserRoleMembership: {
            return renderDrawerContent(
                pageInfo?.id ?? '',
                pageInfo?.name ?? '',
                getUserFullName(pageInfo?.firstName, pageInfo?.lastName),
                userRoleMembershipPostCallback
            );
        }
        case DrawerEntitiesNames.UserProfileRolesMembership: {
            if (currentTenantId !== tenant?.id) {
                return null;
            }
            return renderDrawerContent(
                tenant?.id ?? '',
                tenant?.name ?? '',
                getUserFullName(userDetails?.firstName, userDetails?.lastName),
                userProfileUserRoleMembershipPostCallback
            );
        }
        default: { return null; }
    }
}
